import { useLazyQuery, useMutation } from "@apollo/client";
import React, { useState, useEffect } from "react";
import { clientPolicyIngsight } from "../../App";
import PolicyQueueListComponent from "../../components/PolicyQueueListComponent/PolicyQueueListComponent";
import {
  GET_POLICIES,
  GET_POLICIES_AFTER,
  GET_POLICIES_BEFORE,
} from "../../GraphQL/Queries";
import { GeneralContainer, useStyles } from "./PolicyListContainer.styled";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";
import moment from "moment";
import ModalLoader from "../../components/ModalLoader/ModalLoader";
import { FormControlLabel, IconButton } from "@material-ui/core";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { DeleteTwoTone, EditTwoTone } from "@material-ui/icons";
import {
  CHANGE_POLICY_STATUS,
  CHANGE_SUB_POLICY_STATUS,
} from "../../GraphQL/Mutations";
import { NotificationManager } from "react-notifications";
import ModalConfirmAction from "../../components/ModalConfirmAction/ModalConfirmAction";
import { AntSwitch } from "../../helpers/antSwitch";
import { theme as themeConfig } from "../../themes/themes";

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: themeConfig.neutral.gray[2500],
    margin: "9 0",
    color: "white",
    border: "1px solid #dadde9",
  },
}))(Tooltip);

const PolicyListContainer = ({
  renderAgain,
  setRenderAgain,
  searchText,
  handleEdit,
  user,
  handleAddSubPolicy,
}) => {
  const [data, setData] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const classes = useStyles();
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 20];
  const [page, setPage] = useState(0);
  const [allPages, setAllPages] = useState(0);
  const [startCursor, setStartCursor] = useState("");
  const [endCursor, setEndCursor] = useState("");
  const [changeStatusMainPolicy] = useMutation(CHANGE_POLICY_STATUS, {
    client: clientPolicyIngsight,
  });
  const [changeStatusSubPolicy] = useMutation(CHANGE_SUB_POLICY_STATUS, {
    client: clientPolicyIngsight,
  });
  const [openDeleteModal, setOpenDeleteModal] = useState();

  const columns = [
    {
      title: "ID",
      field: "idMainPolicy",
      type: "string",
    },
    {
      title: "Policy",
      field: "policyName",
      type: "string",
    },
    {
      title: "LOB",
      field: "lobs",
      width: "5%",
      cellStyle: { width: "5%" },
      headerStyle: { width: "5%" },
      render: (rowData) => (
        <div
          style={{
            backgroundColor: themeConfig.primary.purple,
            borderRadius: "99px",
            width: "22px",
            height: "22px",
            justifyContent: "center",
            display: "flex",
            alignItems: "center",
            color: "white",
          }}
        >
          {rowData?.subPolicyCount}
        </div>
      ),
    },
    {
      title: "Created By",
      field: "createUserInfo",
      type: "string",
      render: (rowData) => rowData?.createUserInfo?.fullName,
    },
    {
      title: "Created Date",
      field: "createDate",
      type: "string",
      render: (rowData) => moment(rowData.createDate).format("YYYY-MM-DD"),
    },
    {
      title: "Disable",
      field: "recordStatus",
      cellStyle: {
        padding: "5px 35px",
      },
      render: (rowData) =>
        rowData && (
          <FormControlLabel
            control={
              <AntSwitch
                size="small"
                checked={rowData?.recordStatus === "Active" ? true : false}
                onChange={() => {
                  changeState(rowData);
                  rowData["recordStatus"] =
                    rowData.recordStatus === "Active" ? "Inactive" : "Active";
                  if (rowData["recordStatus"] === "Inactive") {
                    rowData["policySubPolicies"] =
                      rowData.policySubPolicies.map((e) => {
                        return {
                          ...e,
                          recordStatus: "Inactive",
                        };
                      });
                  }
                }}
              />
            }
          />
        ),
    },
    {
      title: "Actions",
      field: "internal_action",
      sorting: false,
      render: (rowData) =>
        rowData && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 0,
            }}
          >
            <Tooltip title="Edit">
              <IconButton
                className={classes.delete}
                onClick={() => handleEdit(rowData)}
              >
                <EditTwoTone style={{ color: themeConfig.text.primary }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                disabled={rowData.recordStatus === "Active"}
                className={classes.delete}
                onClick={() => setOpenDeleteModal(rowData)}
              >
                <DeleteTwoTone
                  style={{
                    color: themeConfig.text.primary,
                    opacity: rowData.recordStatus === "Active" ? 0.5 : 1,
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Sub Policy">
              <IconButton
                className={classes.delete}
                onClick={() => handleAddSubPolicy(rowData)}
              >
                <AddCircleIcon style={{ color: themeConfig.text.primary }} />
              </IconButton>
            </Tooltip>
          </div>
        ),
    },
  ];

  const [runQueryGetPolicies] = useLazyQuery(GET_POLICIES, {
    fetchPolicy: "network-only",
    client: clientPolicyIngsight,
    onCompleted: (response) => {
      assignData(response);
    },
    onError: (error) => {
      setShowLoader(false);
    },
  });

  const [runQueryGetPoliciesAfter] = useLazyQuery(GET_POLICIES_AFTER, {
    fetchPolicy: "network-only",
    client: clientPolicyIngsight,
    onCompleted: (response) => {
      assignData(response);
    },
    onError: (error) => {
      setShowLoader(false);
    },
  });

  const [runQueryGetPoliciesBefore] = useLazyQuery(GET_POLICIES_BEFORE, {
    fetchPolicy: "network-only",
    client: clientPolicyIngsight,
    onCompleted: (response) => {
      assignData(response);
    },
    onError: (error) => {
      setShowLoader(false);
    },
  });

  useEffect(() => {
    setShowLoader(true);
    runQueryGetPolicies({
      variables: {
        first: rowsPerPage,
        search: searchText,
      },
    });
    setPage(0);
  }, [searchText, renderAgain]);

  const assignData = (response) => {
    if (response?.policies?.nodes) {
      let policies = response.policies.nodes.map((record) => {
        let subPolicies = record.policySubPolicies.map((e) => {
          return {
            lob: e.lobs,
          };
        });
        let subPolicyCount = subPolicies.map((e) => e.lob.length);
        return {
          ...record,
          subPolicyCount: subPolicyCount.reduce((a, b) => a + b, 0),
        };
      });
      setData(policies);
      setAllPages(response.policies.totalCount);
      if (response.policies.pageInfo.hasNextPage === true) {
        setEndCursor(response.policies.pageInfo.endCursor);
      }
      if (response.policies.pageInfo.hasPreviousPage === true) {
        setStartCursor(response.policies.pageInfo.startCursor);
      }
    }
    setShowLoader(false);
  };

  const handleChangeRowsPerPage = (item) => {
    let value = item.target.value ? parseInt(item.target.value) : 5;
    setRowsPerPage(value);
    setPage(0);
    runQueryGetPolicies({
      variables: {
        first: value,
        searchText: "",
      },
    });
  };

  const handleChangePage = (item, newPage) => {
    if (newPage > page) {
      setShowLoader(true);
      runQueryGetPoliciesAfter({
        variables: {
          search: searchText ? searchText : "",
          first: rowsPerPage,
          after: endCursor,
        },
      });
    }
    if (newPage < page) {
      setShowLoader(true);
      runQueryGetPoliciesBefore({
        variables: {
          search: searchText ? searchText : "",
          last: rowsPerPage,
          before: startCursor,
        },
      });
    }
    setPage(newPage);
  };

  const changeState = (rowData) => {
    if (rowData?.idSubPolicy) {
      changeStateSubPolicy(rowData);
    } else {
      changeStateMainPolicy(rowData);
    }
  };

  const changeStateSubPolicy = ({ idSubPolicy, recordStatus }) => {
    setShowLoader(true);
    changeStatusSubPolicy({
      variables: {
        policyId: idSubPolicy,
        newPolicyStatus: recordStatus === "Active" ? "INACTIVE" : "ACTIVE",
      },
    })
      .then((res) => {
        if (res?.data?.changeStatusSubPolicy?.idSubPolicy) {
          NotificationManager.success("Sub Policy status updated successfully");
        }
        setShowLoader(false);
      })
      .catch((error) => {
        NotificationManager.error(error.message, "", 5000);

        setShowLoader(false);
      });
  };

  const changeStateMainPolicy = ({ idMainPolicy, recordStatus }) => {
    setShowLoader(true);
    changeStatusMainPolicy({
      variables: {
        policyId: idMainPolicy,
        newPolicyStatus: recordStatus === "Active" ? "INACTIVE" : "ACTIVE",
      },
    })
      .then((res) => {
        if (res?.data?.changeStatusMainPolicy?.idMainPolicy) {
          NotificationManager.success("Policy status updated successfully");
        }
        setShowLoader(false);
      })
      .catch((error) => {
        NotificationManager.error(error.message, "", 5000);

        setShowLoader(false);
      });
  };

  const handleDelete = () => {
    deleteHandler(openDeleteModal);
    setOpenDeleteModal(null);
  };

  const deleteHandler = (rowData) => {
    if (rowData?.idSubPolicy) {
      let mainPolicy = data.find(
        (obj) => obj.idMainPolicy === rowData?.mainPolicyId
      );
      if (mainPolicy?.policySubPolicies?.length > 1) {
        deleteSubPolicy(rowData);
      } else {
        NotificationManager.warning(
          "Main policy should have atleast one sub policy."
        );
      }
    } else {
      deleteMainPolicy(rowData);
    }
  };

  const deleteMainPolicy = ({ idMainPolicy }) => {
    setShowLoader(true);
    changeStatusMainPolicy({
      variables: {
        policyId: idMainPolicy,
        newPolicyStatus: "DELETED",
      },
    })
      .then((res) => {
        if (res?.data?.changeStatusMainPolicy?.idMainPolicy) {
          NotificationManager.success("Policy deleted successfully");
          //setRenderAgain(!renderAgain)
          let records = data.filter((e) => e.idMainPolicy !== idMainPolicy);
          setData(records);
        }
        setShowLoader(false);
      })
      .catch((error) => {
        NotificationManager.error(error.message, "", 5000);

        setShowLoader(false);
      });
  };

  const deleteSubPolicy = ({ idSubPolicy }) => {
    setShowLoader(true);
    changeStatusSubPolicy({
      variables: {
        policyId: idSubPolicy,
        newPolicyStatus: "DELETED",
      },
    })
      .then((res) => {
        if (res?.data?.changeStatusSubPolicy?.idSubPolicy) {
          NotificationManager.success("Sub Policy deleted successfully");
          //setRenderAgain(!renderAgain)
          let records = data.map((e) => {
            return {
              ...e,
              policySubPolicies: e.policySubPolicies.filter(
                (e) => e.idSubPolicy !== idSubPolicy
              ),
            };
          });
          setData(records);
        }
        setShowLoader(false);
      })
      .catch((error) => {
        NotificationManager.error(error.message, "", 5000);

        setShowLoader(false);
      });
  };

  const showDeleteModal = () => {
    return openDeleteModal ? (
      <ModalConfirmAction
        title={"Are you sure you want to delete this policy?"}
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        fn={handleDelete}
      />
    ) : null;
  };

  return (
    <GeneralContainer>
      <PolicyQueueListComponent
        data={data}
        columns={columns}
        page={page}
        allPages={allPages}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleChangePage={handleChangePage}
        changeState={changeState}
        setOpenDeleteModal={setOpenDeleteModal}
        handleEdit={handleEdit}
        selectedMenu={"policy"}
      />
      {showLoader && <ModalLoader />}
      {showDeleteModal()}
    </GeneralContainer>
  );
};

export default PolicyListContainer;
